import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Instagram, Facebook, ChevronDown, MessageCircle, ArrowUpRight } from "lucide-react";
import { SOCIAL, WHATSAPP } from "@/lib/api";
import { useSite } from "./SiteLayout";

export const Wordmark = ({ light }) => (
  <img src={light ? "/brand/logo-white.webp" : "/brand/logo-grey.webp"} alt="Studio Fotografico Santarelli · Fotografi dal 1982"
    className="h-10 sm:h-12 w-auto select-none" data-testid="site-wordmark" />
);

const slugId = (n) => (n.to === "/" ? "home" : n.to.replace(/^\/|\W+/g, "") || n.id);
const Item = ({ n, cls, testid, children }) => (n.external
  ? <a href={n.to} target="_blank" rel="noreferrer" className={cls({})} data-testid={testid}>{children || n.label}</a>
  : <NavLink to={n.to} end={n.to === "/"} className={cls} data-testid={testid}>{children || n.label}</NavLink>);
const topCls = ({ isActive }) => `link-u text-[12.5px] tracking-[0.06em] font-medium py-2 whitespace-nowrap transition-[opacity,color] duration-500 ${isActive ? "opacity-100 after:!w-full" : "opacity-75 hover:opacity-100"}`;

const Dropdown = ({ n }) => (
  <div className="relative group">
    <span className="inline-flex items-center gap-1.5"><Item n={n} cls={topCls} testid={`nav-link-${slugId(n)}`} /><ChevronDown size={12} strokeWidth={1.6} className="opacity-70 transition-transform duration-300 group-hover:rotate-180" /></span>
    <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 transition-[opacity,transform] duration-300">
      <div className="w-[300px] bg-seta text-ink border border-line shadow-[0_24px_60px_rgba(30,28,26,0.10)] py-3" data-testid={`nav-dropdown-${slugId(n)}`}>
        <p className="px-6 pt-2 pb-4 mb-1 border-b border-line eyebrow !text-[10px] flex items-center gap-3"><span className="w-5 h-px bg-oro" />{n.label}</p>
        {n.children.map((c) => (
          <Item key={c.id} n={c} testid={`nav-sub-${slugId(c)}`} cls={() => "group/s flex items-center justify-between px-6 py-3 text-[15px] font-serif font-light transition-[background-color,color,padding-left] duration-500 hover:bg-sabbia/70 hover:text-oliva hover:pl-7"}>
            {c.label}<ArrowUpRight size={13} strokeWidth={1.4} className="text-oro opacity-0 group-hover/s:opacity-100 transition-opacity duration-500" />
          </Item>))}
      </div>
    </div>
  </div>
);

const MobileMenu = ({ menu }) => {
  const [openSub, setOpenSub] = useState(null);
  return (
    <div className="lg:hidden absolute inset-x-0 top-full h-[calc(100svh-5rem)] sm:h-[calc(100svh-6.5rem)] bg-seta overflow-y-auto border-t border-line animate-in fade-in duration-500" data-testid="mobile-menu">
      <div className="container-x py-10 min-h-full flex flex-col justify-between gap-12">
        <nav className="flex flex-col">
          {menu.map((n) => (
            <div key={n.id} className="border-b border-line py-4">
              <div className="flex items-center justify-between">
                <Item n={n} cls={() => "font-serif font-light text-[32px] leading-tight text-ink"} testid={`mobile-nav-${slugId(n)}`} />
                {n.children.length > 0 && <button onClick={() => setOpenSub(openSub === n.id ? null : n.id)} className="p-2 text-oro" aria-label={`Mostra ${n.label}`} aria-expanded={openSub === n.id} data-testid={`mobile-sub-toggle-${slugId(n)}`}>
                  <ChevronDown size={18} strokeWidth={1.4} className={`transition-transform duration-500 ${openSub === n.id ? "rotate-180" : ""}`} /></button>}
              </div>
              {openSub === n.id && <div className="grid grid-cols-2 gap-x-4 gap-y-3 pt-5 pb-2">{n.children.map((c) => <Item key={c.id} n={c} cls={() => "text-[15px] font-light text-stoneish"} testid={`mobile-sub-${slugId(c)}`} />)}</div>}
            </div>))}
          <Link to="/area-clienti" data-testid="mobile-nav-area" className="pt-7 text-[11px] uppercase tracking-[0.22em] text-oliva">Area clienti</Link>
        </nav>
        <div className="space-y-6">
          <a href={WHATSAPP} target="_blank" rel="noreferrer" className="w-full py-4 bg-oro text-ink text-[11.5px] uppercase tracking-[0.2em] font-semibold flex items-center justify-center gap-2" data-testid="mobile-whatsapp-btn"><MessageCircle size={15} /> Richiedi un preventivo</a>
          <div className="flex gap-7 text-oliva justify-center">
            <a href={SOCIAL.instagram} target="_blank" rel="noreferrer" aria-label="Instagram"><Instagram size={19} strokeWidth={1.4} /></a>
            <a href={SOCIAL.facebook} target="_blank" rel="noreferrer" aria-label="Facebook"><Facebook size={19} strokeWidth={1.4} /></a>
          </div>
        </div>
      </div>
    </div>
  );
};

export const Header = () => {
  const { pathname } = useLocation();
  const { hero, menu } = useSite();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const light = hero && !scrolled && !open;

  useEffect(() => {
    const f = () => setScrolled(window.scrollY > 80);
    f();
    window.addEventListener("scroll", f, { passive: true });
    return () => window.removeEventListener("scroll", f);
  }, []);
  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color,backdrop-filter] duration-700 ${light ? "bg-gradient-to-b from-black/35 via-black/10 to-transparent" : "bg-seta/94 backdrop-blur-xl border-b border-line shadow-[0_4px_20px_rgba(30,28,26,0.03)]"}`}>
      <div className="container-x flex items-center justify-between h-20 sm:h-[92px] gap-6">
        <Link to="/" data-testid="header-logo-link" aria-label="Studio Fotografico Santarelli"><Wordmark light={light} /></Link>
        <nav className={`hidden lg:flex items-center gap-6 xl:gap-8 rounded-full transition-[background-color,padding] duration-700 ${light ? "text-ivory bg-black/22 backdrop-blur-md px-8 py-2.5" : "text-ink"}`} aria-label="Menu principale">
          {menu.map((n) => (n.children.length ? <Dropdown key={n.id} n={n} /> : <Item key={n.id} n={n} cls={topCls} testid={`nav-link-${slugId(n)}`} />))}
          <span className="flex items-center gap-5 pl-7 border-l border-current/20">
            <Link to="/area-clienti" data-testid="nav-area-clienti" className="text-[10.5px] uppercase tracking-[0.2em] whitespace-nowrap opacity-70 hover:opacity-100 transition-opacity duration-500">Area clienti</Link>
            <a href={WHATSAPP} target="_blank" rel="noreferrer" data-testid="nav-quote-cta"
              className={`hidden xl:inline-flex items-center gap-2 px-5 py-2.5 text-[10.5px] uppercase tracking-[0.2em] font-semibold border transition-[background-color,color,border-color] duration-500 ${light ? "border-seta/70 text-seta hover:bg-seta hover:text-ink" : "border-ink text-ink hover:bg-ink hover:text-seta"}`}>
              <MessageCircle size={13} /> Preventivo</a>
          </span>
        </nav>
        <button className={`lg:hidden p-3 -mr-1 transition-colors duration-500 ${light ? "text-ivory bg-black/25 backdrop-blur-md rounded-full" : "text-ink"}`} onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label={open ? "Chiudi il menu" : "Apri il menu"} aria-expanded={open}>
          {open ? <X size={22} strokeWidth={1.4} /> : <Menu size={22} strokeWidth={1.4} />}
        </button>
      </div>
      {open && <MobileMenu menu={menu} />}
    </header>
  );
};
